import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop, isNil } from 'lodash-es';
import { randomInt } from 'client/utils/seed-randomizers';

// Models
import { getVinStylesData, buildStylesBasicPathFromVin, VehicleVinModel } from 'client/data/models/vehicle-vin';
import { ApiMetrics, getMetricApiOptions, withMetrics } from 'client/utils/metrics-hoc';

// Utils
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { EventToolbox } from 'client/utils/event-toolbox';
import { transformVin } from 'site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { venomHistory } from 'client/utils/history/venom-history';
import { getCreateListingUrl } from 'site-modules/shared/utils/private-party/private-party';
import {
  fireAppraisalPixel,
  fireSelectVinPixel,
  fireDecodePixels,
  fireVinDecodeMatchTypePixel,
  fireVinErrorTracking,
} from 'site-modules/shared/utils/appraisal/common-utils';

// Constants
import { TrackingConstant } from 'client/tracking/constant';
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'client/site-modules/shared/constants/appraisal/appraisal';
import { APPRAISE_BY_VIN_TAB_EVENT_DATA } from 'site-modules/shared/components/appraisal/appraisal-tabs/constants';
import { APPRAISAL_STORAGE_KEYS } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/constants';
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import { VIN_TAB } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';

// Context
import { useAppraisalTabsContext } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraisal-tabs-context';

// Components
import { VinTabContent } from 'site-modules/shared/components/appraisal/appraisal-tabs/vin-tab/vin-tab-content';
import { VinTabInput } from 'site-modules/shared/components/appraisal/appraisal-tabs/vin-tab/vin-tab-input';
import { AppraisalTabsSubmitButton } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/shared/appraisal-tabs-submit-button';
import { PrivatePartyButton } from 'site-modules/shared/components/appraisal/private-party-button/private-party-button';
import {
  appraisalTabViewPropType,
  getResolvedAppraisalTabViewProps,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';

export class VinTabContainerUI extends Component {
  state = {
    vinInputValue: this.props.vin,
    isVinValid: validation.validateVIN(this.props.vin),
    isVinValidLength: validation.validateVinLength(this.props.vin),
    wasFormSubmitted: false,
    isSubmitting: false,
  };

  static getDerivedStateFromProps(props, state) {
    const { queryVin, isVinEdited, wasVinPrePopulated, setWasVinPrePopulated } = props;
    const { profileOfferVin, isLookUpStyleView } = getResolvedAppraisalTabViewProps(props);
    const { vinInputValue } = state;

    const prepopulatedVin = transformVin(queryVin || profileOfferVin || '');

    if (prepopulatedVin && !vinInputValue && !isVinEdited) {
      if (prepopulatedVin === profileOfferVin && !isLookUpStyleView && !wasVinPrePopulated) {
        setWasVinPrePopulated(true);
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
          event_data: {
            action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            subaction_name: TrackingConstant.VIN_AUTOFILL,
            creative_id: APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID,
          },
        });
      }

      return {
        vinInputValue: prepopulatedVin,
        isVinValid: validation.validateVIN(prepopulatedVin),
        isVinValidLength: validation.validateVinLength(prepopulatedVin),
      };
    }

    return null;
  }

  componentDidMount() {
    const { storage } = this.props;
    const { vinInputValue } = this.state;
    window.addEventListener('pageshow', this.setDefaultButtonStateForCachedPage);

    if (storage && !vinInputValue) {
      const storedVin = storage.get(APPRAISAL_STORAGE_KEYS.VIN);
      let storageVin;
      try {
        storageVin = JSON.parse(storedVin); // Handles the case when an empty string was stored so we don't use the serialized JSON empty string
      } catch {
        storageVin = storedVin;
      }

      if (storageVin) {
        this.onVinChange({ target: { value: storageVin } });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { isAppraiserPageOpened } = this.props;

    // Reset submitting state when /appraisal-value/ page is opened
    if (isAppraiserPageOpened && !prevProps.isAppraiserPageOpened) {
      this.resetIsSubmitting();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.setDefaultButtonStateForCachedPage);
  }

  onVinChange = e => {
    const { setIsVinEdited, storage } = this.props;
    const value = transformVin(e.target.value?.toString());

    setIsVinEdited(true);

    if (storage) {
      storage.set(APPRAISAL_STORAGE_KEYS.VIN, value);
    }

    this.setState({
      vinInputValue: value,
      isVinValid: validation.validateVIN(value),
      isVinValidLength: validation.validateVinLength(value),
      wasFormSubmitted: false,
      isSubmitting: false,
    });
  };

  onSubmit = async e => {
    const {
      creativeId,
      setContextVin,
      setContextStyleIds,
      setContextMmy,
      setStylesAndVin,
      apiMetrics,
      setColors,
      setModelValue,
    } = this.props;
    const { isSellInCityState, isWIDG1125chal1, isPrivatePartyBtn } = getResolvedAppraisalTabViewProps(this.props);
    const { isVinValid, vinInputValue } = this.state;

    e.preventDefault();

    setContextMmy({});
    setContextStyleIds(null);
    setContextVin('');

    this.setState({
      wasFormSubmitted: true,
      isSubmitting: true,
    });

    if (!isVinValid) {
      this.fireVinErrorTracking(TrackingConstant.SUBACTION_VIN_VALIDATION, TrackingConstant.VIN_FORMAT_INVALID);
      this.resetIsSubmitting();
      return;
    }

    const stylesResponse = await getVinStylesData({
      vin: vinInputValue,
      apiOptions: getMetricApiOptions(apiMetrics),
    });
    setModelValue(buildStylesBasicPathFromVin({ vin: vinInputValue }), VehicleVinModel, stylesResponse);

    const styles = stylesResponse?.results;

    fireDecodePixels({ stylesLength: styles?.length, vinStylesData: stylesResponse, creativeId });
    fireVinDecodeMatchTypePixel(stylesResponse?.matchType, creativeId);

    if (stylesResponse?.isPubStateInvalid) {
      fireVinErrorTracking(vinInputValue, stylesResponse, creativeId);
    }

    if (isWIDG1125chal1 && isSellInCityState && !isPrivatePartyBtn) {
      venomHistory.push(`/sell-car/instant-cash-offer/?icoVin=${vinInputValue}`);
      return;
    }

    const styleIds = styles.map(({ styleId }) => styleId);

    if (isVinValid && styleIds.length) {
      fireSelectVinPixel(vinInputValue, creativeId);
    }
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
      event_data: {
        ...APPRAISE_BY_VIN_TAB_EVENT_DATA,
        creative_id: creativeId,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        subaction_name: TrackingConstant.SUBMIT_VEHICLE,
        value: TrackingConstant.VIN,
      },
    });

    if (styleIds.length) fireAppraisalPixel('VIN', creativeId);

    if (isPrivatePartyBtn) {
      EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.START_PP_LISTING, {
        value: VIN_TAB.label,
        creativeId,
      });

      venomHistory.push(getCreateListingUrl(vinInputValue));
      return;
    }

    setColors({ exteriorColor: stylesResponse?.exteriorColor, interiorColor: stylesResponse?.interiorColor });
    setContextVin(vinInputValue);
    setContextStyleIds(styleIds);
    setStylesAndVin(styles, vinInputValue);
  };

  setDefaultButtonStateForCachedPage = ({ persisted }) => {
    if (persisted) {
      this.resetIsSubmitting();
      this.setState({ wasFormSubmitted: false });
    }
  };

  vinInputId = randomInt();

  fireVinErrorTracking = (subactionName, value) => {
    const { creativeId } = this.props;
    const { vinInputValue } = this.state;

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        ...APPRAISE_BY_VIN_TAB_EVENT_DATA,
        creative_id: creativeId,
        subaction_name: subactionName,
        value,
      },
    });
    const vinErrorValue = `${vinInputValue}_${subactionName}_${value}`;
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        ...APPRAISE_BY_VIN_TAB_EVENT_DATA,
        creative_id: creativeId,
        subaction_name: TrackingConstant.SUBACTION_VIN_ERROR,
        value: vinErrorValue,
      },
    });
  };

  resetIsSubmitting = () => {
    this.setState({ isSubmitting: false });
  };

  render() {
    const {
      vin,
      creativeId,
      setStylesAndVin,
      ctaText,
      isProjectEveStyle,
      vinDecoderStyles,
      isActiveTab,
      view,
      ...restProps
    } = this.props;
    const { isLookUpStyleView, isVdpEmbedded, isBackgroundStyleTabEnabled, isPrivatePartyBtn, profileOfferVin } =
      getResolvedAppraisalTabViewProps(this.props);
    const { vinInputValue, isVinValid, isVinValidLength, wasFormSubmitted, isSubmitting } = this.state;

    const styles = vinDecoderStyles?.results;
    const hasStyles = styles?.length > 0;
    const isDecodedVinInvalid = wasFormSubmitted && isVinValid && !isNil(vinDecoderStyles) && !hasStyles;
    const isError = wasFormSubmitted && (!isVinValid || isDecodedVinInvalid);

    return (
      <VinTabContent
        {...restProps}
        vin={vin}
        vinInputValue={vinInputValue}
        isVinValid={isVinValid}
        wasFormSubmitted={wasFormSubmitted}
        isSubmitting={isSubmitting}
        profileOfferVin={profileOfferVin}
        resetIsSubmitting={this.resetIsSubmitting}
        isDecodedVinInvalid={isDecodedVinInvalid}
        isBackgroundStyleTabEnabled={isBackgroundStyleTabEnabled}
        {...(view ? { view } : {})}
        vinInputId={this.vinInputId}
        inputSlot={
          <VinTabInput
            className={classnames({
              'mb-lg-0': isBackgroundStyleTabEnabled || isProjectEveStyle,
            })}
            vinInputValue={vinInputValue}
            onVinChange={this.onVinChange}
            isError={isError}
            isVinValid={isVinValid}
            isVinValidLength={isVinValidLength}
            isDecodedVinInvalid={isDecodedVinInvalid}
            inputId={this.vinInputId}
          />
        }
        submitButtonSlot={
          isPrivatePartyBtn ? (
            <PrivatePartyButton
              onClick={this.onSubmit}
              data-test="appraise-btn-by-vin"
              isDisabled={isDecodedVinInvalid || isSubmitting}
              isSubmitting={isSubmitting}
              isActiveTab={isActiveTab}
            />
          ) : (
            <AppraisalTabsSubmitButton
              ctaText={ctaText}
              onSubmit={this.onSubmit}
              isSubmitting={isSubmitting}
              containerClassName={classnames({
                'mt-0_5': isLookUpStyleView,
              })}
              buttonClassName={classnames('btn-responsive-md-down', {
                'py-0_5': isVdpEmbedded || isBackgroundStyleTabEnabled || isProjectEveStyle,
              })}
              isDisabled={isDecodedVinInvalid || isSubmitting}
              data-test="appraise-btn-by-vin"
              isActiveTab={isActiveTab}
            />
          )
        }
      />
    );
  }
}

VinTabContainerUI.propTypes = {
  vin: PropTypes.string,
  queryVin: PropTypes.string,
  isVinEdited: PropTypes.bool,
  setIsVinEdited: PropTypes.func,
  setContextVin: PropTypes.func.isRequired,
  setContextStyleIds: PropTypes.func.isRequired,
  setContextMmy: PropTypes.func.isRequired,
  setStylesAndVin: PropTypes.func,
  isLookUpStyleView: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  profileOfferVin: PropTypes.string,
  hasAddressToVinDrawer: PropTypes.bool,
  isBackgroundStyleTabEnabled: PropTypes.bool,
  creativeId: PropTypes.string,
  ctaText: PropTypes.string,
  isVdpEmbedded: PropTypes.bool,
  onFindVinModalOpen: PropTypes.func,
  onFindVinModalClose: PropTypes.func,
  isAppraiserPageOpened: PropTypes.bool,
  isSellInCityState: PropTypes.bool,
  isProjectEveStyle: PropTypes.bool,
  wasVinPrePopulated: PropTypes.bool,
  setWasVinPrePopulated: PropTypes.func,
  vinDecoderStyles: PropTypes.shape({}),
  apiMetrics: ApiMetrics,
  isActiveTab: PropTypes.bool,
  setColors: PropTypes.func.isRequired,
  setModelValue: PropTypes.func,
  isPrivatePartyBtn: PropTypes.bool,
  view: appraisalTabViewPropType,
};

VinTabContainerUI.defaultProps = {
  vin: '',
  queryVin: '',
  isVinEdited: false,
  setIsVinEdited: noop,
  setStylesAndVin: noop,
  isLookUpStyleView: false,
  isFullWidth: false,
  profileOfferVin: '',
  hasAddressToVinDrawer: false,
  isBackgroundStyleTabEnabled: false,
  creativeId: APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID,
  ctaText: 'Appraise My Car',
  isVdpEmbedded: false,
  onFindVinModalOpen: noop,
  onFindVinModalClose: noop,
  isAppraiserPageOpened: false,
  isSellInCityState: false,
  isProjectEveStyle: false,
  wasVinPrePopulated: false,
  setWasVinPrePopulated: noop,
  vinDecoderStyles: null,
  apiMetrics: undefined,
  isActiveTab: false,
  setModelValue: noop,
  isPrivatePartyBtn: false,
  view: null,
};

const VinTabContainerWrapped = withMetrics(VinTabContainerUI);

export function VinTabContainer(props) {
  const {
    vin,
    isVinEdited,
    setIsVinEdited,
    queryVin,
    setVin: setContextVin,
    setStyleIds: setContextStyleIds,
    setMmy: setContextMmy,
    vinDecoderStyles,
    onFindVinModalOpen,
    onFindVinModalClose,
    isAppraiserPageOpened,
    isProjectEveStyle,
    wasVinPrePopulated,
    setWasVinPrePopulated,
    setColors,
  } = useAppraisalTabsContext();

  return (
    <VinTabContainerWrapped
      {...props}
      vin={vin}
      isVinEdited={isVinEdited}
      setIsVinEdited={setIsVinEdited}
      queryVin={queryVin}
      setContextVin={setContextVin}
      setContextStyleIds={setContextStyleIds}
      setContextMmy={setContextMmy}
      onFindVinModalOpen={onFindVinModalOpen}
      onFindVinModalClose={onFindVinModalClose}
      isAppraiserPageOpened={isAppraiserPageOpened}
      isProjectEveStyle={isProjectEveStyle}
      wasVinPrePopulated={wasVinPrePopulated}
      setWasVinPrePopulated={setWasVinPrePopulated}
      vinDecoderStyles={vinDecoderStyles}
      setColors={setColors}
    />
  );
}
